<template>
  <div class="alert-shell">
    <PageLoader :storage="appLoading" />
    <ServerError v-if="ServerError" />
    <v-snackbar v-model="showSnackBar" color="#427D2D" right :timeout="timeout">
      <v-layout wrap justify-center>
        <v-flex text-left class="align-self-center">
          <span style="color: #fff">{{ msg }}</span>
        </v-flex>
        <v-flex text-right>
          <v-btn small :ripple="false" text @click="showSnackBar = false">
            <v-icon style="color: #fff">mdi-close</v-icon>
          </v-btn>
        </v-flex>
      </v-layout>
    </v-snackbar>

    <v-container fluid class="pa-5">
      <!-- ============ hero ============ -->
      <div class="alert-hero">
        <span class="hero-bar"></span>
        <div class="hero-left">
          <div class="hero-badge"><v-icon size="27">mdi-snake</v-icon></div>
          <div class="hero-copy">
            <div class="hero-eyebrow">
              <v-icon size="13">mdi-bell-alert-outline</v-icon>
              Snakebite Alert
            </div>
            <div class="hero-title">{{ data.name || "Snakebite Incident" }}</div>
            <div class="hero-meta">
              <span class="meta-chip">
                <v-icon size="13">mdi-calendar-clock</v-icon>
                {{ formatDateTime(data.reporteddate) || "Date not recorded" }}
              </span>
              <span class="meta-chip" v-if="data.Districtname">
                <v-icon size="13">mdi-map-marker-outline</v-icon>
                {{ data.Districtname }} District
              </span>
              <span class="meta-chip" v-if="data.phone || data.mobileno">
                <v-icon size="13">mdi-phone-outline</v-icon>
                {{ data.phone || data.mobileno }}
              </span>
            </div>
          </div>
        </div>
        <div class="hero-right">
          <span :class="['status-chip', statusClass(data.responseStatus)]">
            <span class="status-dot"></span>
            {{ data.responseStatus || "New" }}
          </span>
          <div class="hero-actions" v-if="canAct && data.responseStatus !== 'Closed'">
            <v-btn small depressed class="btn-solid" @click="openActionDialog()">
              <v-icon small left>mdi-clipboard-plus-outline</v-icon>
              <span class="btnlabel">Record Action</span>
            </v-btn>
            <v-btn small depressed class="btn-ghost" @click="closeDialog = true">
              <v-icon small left>mdi-check-decagram-outline</v-icon>
              <span class="btnlabel">Close Incident</span>
            </v-btn>
          </div>
        </div>
      </div>

      <!-- ============ response flow ============ -->
      <div class="panel">
        <div class="panel-head">
          <div class="panel-icon"><v-icon size="19">mdi-transit-connection-horizontal</v-icon></div>
          <div class="panel-copy">
            <div class="cardtitle">Response Flow</div>
            <div class="subtext">How this incident moved from report to closure</div>
          </div>
          <div class="panel-actions flow-facts">
            <span class="fact-chip" v-for="(fact, i) in flowFacts" :key="i">
              <v-icon size="14">{{ fact.icon }}</v-icon>
              {{ fact.text }}
            </span>
          </div>
        </div>

        <ol class="flow">
          <li
            v-for="(step, i) in responseFlow"
            :key="i"
            :class="['flow-step', step.state]"
          >
            <div class="flow-rail">
              <span class="flow-node"><v-icon size="16">{{ step.icon }}</v-icon></span>
              <!-- each stage owns the connector leaving it, so the trail runs
                   emerald as far as the incident actually got -->
              <span class="flow-line" v-if="i < responseFlow.length - 1"></span>
            </div>
            <div class="flow-copy">
              <div class="flow-label">{{ step.label }}</div>
              <div class="flow-value">{{ step.value }}</div>
              <div class="flow-stamp" v-if="step.stamp">{{ step.stamp }}</div>
            </div>
          </li>
        </ol>
      </div>

      <!-- ============ incident details ============ -->
      <div class="panel">
        <div class="panel-head">
          <div class="panel-icon"><v-icon size="19">mdi-clipboard-text-outline</v-icon></div>
          <div class="panel-copy">
            <div class="cardtitle">Incident Details</div>
            <div class="subtext">Victim and incident information as reported</div>
          </div>
          <div class="panel-actions" v-if="canEditReport">
            <v-btn
              x-small
              depressed
              class="btn-solid"
              @click="openEditReportDialog()"
            >
              <v-icon x-small left>mdi-pencil</v-icon>
              <span class="btnlabel">Edit Report</span>
            </v-btn>
          </div>
        </div>

        <v-layout wrap justify-start>
          <v-flex xs12 sm6 md4 lg3 pa-2 v-for="(field, i) in detailFields" :key="i">
            <div class="detail-cell">
              <div class="detail-icon"><v-icon size="15">{{ field.icon }}</v-icon></div>
              <div class="detail-copy">
                <div class="itemKey">{{ field.label }}</div>
                <div class="itemValue">{{ field.value || "—" }}</div>
              </div>
            </div>
          </v-flex>
        </v-layout>

        <div class="note-block" v-if="data.notes || data.note">
          <div class="note-head">
            <v-icon size="15">mdi-note-text-outline</v-icon>
            <span class="itemKey">Notes</span>
          </div>
          <div class="note-body">{{ data.notes || data.note }}</div>
        </div>

        <div class="note-block" v-if="data.info">
          <div class="note-head">
            <v-icon size="15">mdi-information-outline</v-icon>
            <span class="itemKey">Additional Info</span>
          </div>
          <div class="note-body">{{ data.info }}</div>
        </div>

        <div class="note-block note-block--closed" v-if="closureRemarks">
          <div class="note-head">
            <v-icon size="15">mdi-check-decagram-outline</v-icon>
            <span class="itemKey">Closure Remarks</span>
          </div>
          <div class="note-body">{{ closureRemarks }}</div>
        </div>

        <div class="pt-4" v-if="data.photo">
          <div class="note-head pb-2">
            <v-icon size="15">mdi-image-outline</v-icon>
            <span class="itemKey">Photograph</span>
          </div>
          <div v-viewer="{ movable: false }">
            <img :src="mediaURL + data.photo" class="alert-photo clickable" />
          </div>
        </div>
      </div>

      <!-- ============ nearest hospitals ============ -->
      <div class="panel">
        <div class="panel-head">
          <div class="panel-icon"><v-icon size="19">mdi-hospital-building</v-icon></div>
          <div class="panel-copy">
            <div class="cardtitle">Nearest Medical Facilities</div>
            <div class="subtext">
              <span v-if="hospitalSearchMode === 'nearest'">
                Sorted by distance from the incident location
              </span>
              <span v-else>
                No coordinates on this report — showing hospitals listed in
                {{ data.Districtname || "the district" }}
              </span>
            </div>
          </div>
          <div class="panel-actions" v-if="hasLocation">
            <v-select
              v-model="radius"
              :items="radiusOptions"
              item-text="text"
              item-value="value"
              dense
              outlined
              hide-details
              class="radius-select"
            ></v-select>
          </div>
        </div>

        <v-layout wrap justify-start v-if="hospitals && hospitals.length > 0">
          <v-flex
            xs12
            sm6
            md4
            pa-2
            v-for="(hospital, index) in hospitals"
            :key="hospital._id || index"
          >
            <div class="hospital-card">
              <div class="hosp-top">
                <div class="hosp-icon"><v-icon size="17">mdi-hospital-box-outline</v-icon></div>
                <div class="hosp-copy">
                  <div class="hospital-name">{{ hospital.name }}</div>
                  <div class="hospital-meta" v-if="hospital.category">
                    {{ hospital.category }}
                    <span v-if="hospital.adminverified === false" class="unverified-tag ml-1">
                      unverified
                    </span>
                  </div>
                </div>
                <div class="distance-pill" v-if="hospital.distanceInKm !== null">
                  {{ hospital.distanceInKm }} km
                </div>
              </div>
              <div class="hosp-addr">
                <v-icon size="13">mdi-map-marker-outline</v-icon>
                <span>{{ hospital.address || "—" }}</span>
              </div>
              <div
                v-if="hospital.availableDoctorsCount > 0"
                class="doctors-badge mt-2 cursor-pointer"
                @click="openDoctorsModal(hospital)"
                title="Click to view doctor details"
              >
                <div class="d-flex align-center justify-space-between w-100">
                  <div class="d-flex align-center">
                    <div class="doc-icon-circle mr-2">
                      <v-icon size="13" color="#427D2D">mdi-doctor</v-icon>
                    </div>
                    <span class="doc-badge-text">
                      <strong class="doc-count-num">{{ hospital.availableDoctorsCount }}</strong> Available Doctor{{ hospital.availableDoctorsCount === 1 ? '' : 's' }}
                    </span>
                  </div>
                  <div class="doc-view-btn">
                    <span>Details</span>
                    <v-icon x-small color="#427D2D" class="ml-1">mdi-chevron-right</v-icon>
                  </div>
                </div>
              </div>
              <div class="hosp-actions">
                <v-btn
                  x-small
                  depressed
                  class="btn-solid"
                  :disabled="!hospital.callNumber"
                  :href="hospital.callNumber ? 'tel:' + hospital.callNumber : null"
                >
                  <v-icon x-small left>mdi-phone</v-icon>
                  <span class="btnlabel">{{ hospital.mobileno || "No number" }}</span>
                </v-btn>
              </div>
            </div>
          </v-flex>
        </v-layout>
        <div v-else class="empty-state">
          <v-icon size="30">mdi-hospital-marker</v-icon>
          <div class="nodata pt-2">No hospitals found for this location.</div>
        </div>
      </div>

      <!-- ============ map ============ -->
      <div class="panel" v-if="hasLocation">
        <div class="panel-head">
          <div class="panel-icon"><v-icon size="19">mdi-map-marker-radius-outline</v-icon></div>
          <div class="panel-copy">
            <div class="cardtitle">Location</div>
            <div class="subtext">Incident site and the facilities listed above</div>
          </div>
        </div>
        <div id="alertmap"></div>
      </div>

      <!-- ============ action log ============ -->
      <div class="panel">
        <div class="panel-head">
          <div class="panel-icon"><v-icon size="19">mdi-history</v-icon></div>
          <div class="panel-copy">
            <div class="cardtitle">Action Log</div>
            <div class="subtext">Every action recorded against this incident, newest first</div>
          </div>
          <div class="panel-actions">
            <v-btn
              v-if="canAct && data.responseStatus !== 'Closed'"
              x-small
              depressed
              class="btn-solid"
              @click="openActionDialog()"
            >
              <v-icon x-small left>mdi-plus</v-icon>
              <span class="btnlabel">Add</span>
            </v-btn>
          </div>
        </div>

        <div class="timeline" v-if="filteredActions && filteredActions.length > 0">
          <div class="timeline-item" v-for="(action, index) in filteredActions" :key="index">
            <div class="timeline-dot">
              <v-icon size="14">{{ actionIcon(action.actionType) }}</v-icon>
            </div>
            <div class="timeline-body">
              <div class="timeline-top">
                <div class="timeline-title">
                  <span class="action-type">{{ action.actionType }}</span>
                  <span class="action-time">
                    <v-icon size="12">mdi-clock-outline</v-icon>
                    {{ formatDateTime(action.actionTime) }}
                  </span>
                </div>
                <div class="timeline-tools" v-if="canEditAction(action)">
                  <v-icon x-small color="#047857" @click="editAction(action)">mdi-pencil</v-icon>
                  <v-icon x-small color="#c62828" @click="confirmDeleteAction(action)">
                    mdi-delete
                  </v-icon>
                </div>
              </div>
              <div class="action-desc">{{ action.description }}</div>
              <div class="action-meta">
                <v-icon size="12">mdi-account-outline</v-icon>
                {{ action.actionByName || (action.actionBy && action.actionBy.name) || "—" }}
                <span v-if="action.actionByRole"> ({{ action.actionByRole }})</span>
                <span v-if="action.hospitalName">&nbsp;·&nbsp; {{ action.hospitalName }}</span>
                <span v-if="action.contactedNumber">&nbsp;·&nbsp; {{ action.contactedNumber }}</span>
              </div>
              <div class="detail-pills" v-if="action.details && action.details.length > 0">
                <span class="detail-pill" v-for="(detail, di) in action.details" :key="di">
                  <b>{{ detail.key }}:</b> {{ detail.value }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="empty-state">
          <v-icon size="30">mdi-clipboard-text-clock-outline</v-icon>
          <div class="nodata pt-2">No actions recorded yet.</div>
        </div>
      </div>

      <!-- ============ report edit log ============ -->
      <div class="panel">
        <div class="panel-head">
          <div class="panel-icon"><v-icon size="19">mdi-file-document-edit-outline</v-icon></div>
          <div class="panel-copy">
            <div class="cardtitle">Report Edit Log</div>
            <div class="subtext">History of edits made to victim report details</div>
          </div>
        </div>

        <div class="timeline" v-if="editLogs && editLogs.length > 0">
          <div class="timeline-item" v-for="(log, index) in editLogs" :key="index">
            <div class="timeline-dot">
              <v-icon size="14">mdi-file-document-edit-outline</v-icon>
            </div>
            <div class="timeline-body">
              <div class="timeline-top">
                <div class="timeline-title">
                  <span class="action-type">Report Details Updated</span>
                  <span class="action-time">
                    <v-icon size="12">mdi-clock-outline</v-icon>
                    {{ formatDateTime(log.actionTime) }}
                  </span>
                </div>
              </div>
              <div class="action-desc">{{ log.description }}</div>
              <div class="action-meta">
                <v-icon size="12">mdi-account-outline</v-icon>
                {{ log.actionByName || (log.actionBy && log.actionBy.name) || "—" }}
                <span v-if="log.actionByRole"> ({{ log.actionByRole }})</span>
              </div>
              <div class="detail-pills" v-if="log.details && log.details.length > 0">
                <span class="detail-pill" v-for="(detail, di) in log.details" :key="di">
                  <b>{{ detail.key }}:</b> {{ detail.value }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="empty-state">
          <v-icon size="30">mdi-file-document-edit-outline</v-icon>
          <div class="nodata pt-2">No report edits recorded yet.</div>
        </div>
      </div>
      <!-- Alert Recipients panel, disabled for now. Re-enable by deleting the
           surrounding comment markers; the markup already matches the panels above.
      <div class="panel">
        <div class="panel-head">
          <div class="panel-icon"><v-icon size="19">mdi-account-multiple-outline</v-icon></div>
          <div class="panel-copy">
            <div class="cardtitle">Alert Recipients</div>
            <div class="subtext">Which officers were notified, and how fast each responded</div>
          </div>
        </div>
        <div style="overflow-x: auto" v-if="recipients && recipients.length > 0">
          <v-simple-table>
            <template v-slot:default>
              <thead>
          <tr>
            <th class="text-left tablehead">Officer</th>
            <th class="text-left tablehead">Role</th>
            <th class="text-left tablehead">Level</th>
            <th class="text-left tablehead">Alerted</th>
            <th class="text-left tablehead">Opened</th>
            <th class="text-left tablehead">Actions</th>
            <th class="text-left tablehead">Response Time</th>
          </tr>
              </thead>
              <tbody>
          <tr v-for="(rec, index) in recipients" :key="index">
            <td class="tableitems">{{ rec.recipientName || "-" }}</td>
            <td class="tableitems">{{ rec.recipientRole }}</td>
            <td class="tableitems">{{ rec.level }}</td>
            <td class="tableitems">{{ formatDateTime(rec.sentDate) }}</td>
            <td class="tableitems">
              <span v-if="rec.isViewed">{{ formatDateTime(rec.viewedDate) }}</span>
              <span v-else class="pending-text">Not opened</span>
            </td>
            <td class="tableitems">{{ rec.actionCount || 0 }}</td>
            <td class="tableitems">
              <span v-if="rec.responseTime !== null && rec.responseTime !== undefined">
                {{ humanize(rec.responseTime) }}
              </span>
              <span v-else>-</span>
            </td>
          </tr>
              </tbody>
            </template>
          </v-simple-table>
        </div>
        <div v-else class="empty-state">
          <v-icon size="30">mdi-account-off-outline</v-icon>
          <div class="nodata pt-2">No officers were alerted for this incident.</div>
          <v-btn x-small depressed class="btn-ghost mt-3" v-if="canAct" @click="resendAlert()">
            <span class="btnlabel">Send Alert Now</span>
          </v-btn>
        </div>
      </div>
      -->
    </v-container>

    <!-- record / edit action -->
    <v-dialog v-model="actionDialog" max-width="640" persistent>
      <v-card rounded="lg">
        <div class="dialog-title">
          <v-icon size="19">mdi-clipboard-plus-outline</v-icon>
          <span class="cardtitle">{{ editId ? "Edit Action" : "Record Action" }}</span>
        </div>
        <v-card-text>
          <v-layout wrap justify-start>
            <v-flex xs12 pb-3>
              <div class="itemKey pb-1">Action</div>
              <v-select
                v-model="form.actionType"
                :items="actionTypes"
                outlined
                dense
                hide-details
                :disabled="!!editId"
              ></v-select>
            </v-flex>

            <v-flex xs12 sm6 pb-3 pr-sm-2>
              <div class="itemKey pb-1">Date</div>
              <v-menu
                v-model="menuActionDate"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
                :disabled="!!editId"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="form.actionDate"
                    readonly
                    v-bind="attrs"
                    v-on="editId ? {} : on"
                    :disabled="!!editId"
                    outlined
                    dense
                    hide-details
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="form.actionDate"
                  :min="minActionDate"
                  :max="maxActionDate"
                  @input="menuActionDate = false"
                ></v-date-picker>
              </v-menu>
            </v-flex>

            <v-flex xs12 sm6 pb-3>
              <div class="itemKey pb-1">Time</div>
              <v-menu
                v-model="menuActionTime"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                max-width="290px"
                min-width="290px"
                :disabled="!!editId"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="form.actionTime"
                    readonly
                    v-bind="attrs"
                    v-on="editId ? {} : on"
                    :disabled="!!editId"
                    outlined
                    dense
                    hide-details
                  ></v-text-field>
                </template>
                <v-time-picker
                  v-model="form.actionTime"
                  format="24hr"
                  @click:minute="menuActionTime = false"
                ></v-time-picker>
              </v-menu>
            </v-flex>

            <v-flex xs12 pb-3>
              <div class="itemKey pb-1">{{ actionDescriptionLabel }}</div>
              <v-textarea
                v-model="form.description"
                outlined
                dense
                rows="3"
                hide-details
                :placeholder="actionDescriptionPlaceholder"
              ></v-textarea>
            </v-flex>

            <v-flex xs12 pb-3 v-if="form.hospitalName">
              <div class="itemKey pb-1">Hospital</div>
              <div class="itemValue">
                {{ form.hospitalName }}
                <span v-if="form.contactedNumber"> ({{ form.contactedNumber }})</span>
                <v-icon x-small color="error" class="ml-2" @click="clearHospital()"
                  >mdi-close-circle</v-icon
                >
              </div>
            </v-flex>

            <!-- dynamic key/value pairs -->
            <v-flex xs12>
              <v-layout wrap justify-space-between align-center pb-2>
                <v-flex xs8>
                  <div class="itemKey">Additional Details</div>
                </v-flex>
                <v-flex xs4 class="text-right">
                  <v-btn x-small depressed class="btn-ghost" @click="addDetailRow()">
                    <v-icon x-small left>mdi-plus</v-icon>
                    <span class="btnlabel">Add Field</span>
                  </v-btn>
                </v-flex>
              </v-layout>
              <v-layout
                wrap
                justify-start
                align-center
                v-for="(detail, index) in form.details"
                :key="index"
                class="pb-2"
              >
                <v-flex xs5 pr-2>
                  <v-text-field
                    v-model="detail.key"
                    placeholder="Field"
                    outlined
                    dense
                    hide-details
                  ></v-text-field>
                </v-flex>
                <v-flex xs6 pr-2>
                  <v-text-field
                    v-model="detail.value"
                    placeholder="Value"
                    outlined
                    dense
                    hide-details
                  ></v-text-field>
                </v-flex>
                <v-flex xs1>
                  <v-icon small color="error" @click="removeDetailRow(index)"
                    >mdi-close-circle</v-icon
                  >
                </v-flex>
              </v-layout>
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="#FF1313" @click="actionDialog = false">
            <span class="btntext">Cancel</span>
          </v-btn>
          <v-btn depressed class="btn-solid" @click="submitAction()">
            <span class="btnlabel">{{ editId ? "Update Action" : "Save Action" }}</span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="440">
      <v-card rounded="lg">
        <div class="dialog-title">
          <v-icon size="19" color="#c62828">mdi-alert-circle-outline</v-icon>
          <span class="cardtitle">Confirmation</span>
        </div>
        <v-card-text>Remove this action from the log?</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="#FF1313" @click="deleteDialog = false">
            <span class="btntext">Cancel</span>
          </v-btn>
          <v-btn depressed class="btn-solid" @click="deleteAction()">
            <span class="btnlabel">Delete</span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="closeDialog" max-width="500">
      <v-card rounded="lg">
        <div class="dialog-title">
          <v-icon size="19">mdi-check-decagram-outline</v-icon>
          <span class="cardtitle">Close Incident</span>
        </div>
        <v-card-text>
          <div class="itemKey pb-1">Closing remarks</div>
          <v-textarea v-model="closeRemarks" outlined dense rows="3" hide-details></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="#FF1313" @click="closeDialog = false">
            <span class="btntext">Cancel</span>
          </v-btn>
          <v-btn depressed class="btn-solid" @click="closeIncident()">
            <span class="btnlabel">Close Incident</span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- DOCTORS DETAILS MODAL -->
    <v-dialog v-model="doctorsModal" max-width="680" transition="dialog-bottom-transition">
      <v-card rounded="xl" class="overflow-hidden">
        <!-- HEADER BANNER -->
        <div class="doc-modal-header pa-4 d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <div class="header-icon-box mr-3">
              <v-icon color="#ffffff" size="22">mdi-hospital-building</v-icon>
            </div>
            <div>
              <div class="doc-modal-title">{{ selectedHospitalName }}</div>
              <div class="doc-modal-subtitle">
                <v-icon x-small color="#a7f3d0" class="mr-1">mdi-doctor</v-icon>
                Hospital Doctor Directory & Availability Status
              </div>
            </div>
          </div>
          <v-btn icon color="white" class="close-btn-round" @click="doctorsModal = false">
            <v-icon size="20">mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text class="pa-4 pt-5 style-scrollbar" style="max-height: 480px; overflow-y: auto;">
          <div v-if="selectedHospitalDoctors && selectedHospitalDoctors.length > 0">
            <div
              class="doc-card-item mb-3 pa-4"
              v-for="(doc, idx) in selectedHospitalDoctors"
              :key="doc._id || idx"
            >
              <div class="d-flex align-center justify-space-between flex-wrap">
                <!-- LEFT SECTION: Avatar + Info -->
                <div class="d-flex align-center">
                  <!-- <div class="doc-avatar-box mr-3">
                    <v-icon size="26" color="#2d6a4f">mdi-account-doctor</v-icon>
                  </div> -->
                  <div>
                    <div class="doc-name-text">
                      Dr. {{ doc.name }}
                      <span class="doc-spec-tag ml-2">{{ doc.specialization || "General Physician" }}</span>
                    </div>
                    <div class="doc-meta-info mt-1 d-flex align-center flex-wrap">
                      <span class="meta-item mr-3">
                        <v-icon x-small color="#52b788" class="mr-1">mdi-map-marker-outline</v-icon>
                        District: <strong>{{ doc.district }}</strong>
                      </span>
                      <span class="meta-item" v-if="doc.dateOfJoining">
                        <v-icon x-small color="#52b788" class="mr-1">mdi-calendar-check-outline</v-icon>
                        Joined: {{ formatDate(doc.dateOfJoining) }}
                      </span>
                    </div>
                  </div>
                </div>

                <!-- RIGHT SECTION: Status Chip + Dial Button -->
                <div class="d-flex flex-column align-end pt-2 pt-sm-0">
                  <div
                    :class="doc.status === 'Available' ? 'status-pill-available' : 'status-pill-unavailable'"
                    class="mb-2"
                  >
                    <v-icon x-small color="#ffffff" class="mr-1">
                      {{ doc.status === 'Available' ? 'mdi-check-circle' : 'mdi-clock-outline' }}
                    </v-icon>
                    <span>{{ doc.status }}</span>
                  </div>

                  <v-btn
                    small
                    depressed
                    class="call-doc-btn"
                    :disabled="!doc.phone"
                    :href="doc.phone ? 'tel:' + doc.phone : null"
                  >
                    <v-icon small left color="#ffffff">mdi-phone</v-icon>
                    <span style="color: #ffffff !important; font-weight: 600; font-family: poppinsmedium;">
                      {{ doc.phone || "No phone" }}
                    </span>
                  </v-btn>
                </div>
              </div>
            </div>
          </div>

          <div v-else class="text-center py-8">
            <div class="empty-doc-box">
              <v-icon size="42" color="#74c69d">mdi-doctor</v-icon>
              <div class="nodata pt-3 font-weight-bold" style="color: #2d6a4f;">No Doctors Listed</div>
              <div class="caption text--secondary">No doctor details are currently registered for this medical facility.</div>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- EDIT REPORT DIALOG -->
    <v-dialog v-model="editReportDialog" max-width="720" persistent>
      <v-card rounded="lg">
        <v-card-title class="d-flex align-center justify-space-between pa-4">
          <div class="d-flex align-center">
            <v-icon color="#427D2D" class="mr-2">mdi-pencil-box-multiple-outline</v-icon>
            <span class="heading" style="font-size:18px;">Edit Snake Bite Incident Report</span>
          </div>
          <v-btn icon color="red" @click="editReportDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-4 style-scrollbar" style="max-height: 520px; overflow-y: auto;">
          <v-form ref="editReportForm">
            <v-layout wrap justify-start>
              <v-flex xs12 sm6 md6 lg6 pr-sm-2 class="pb-3">
                <span class="tablehead" style="color:black">Victim Name</span>
                <v-text-field
                  outlined
                  dense
                  hide-details
                  v-model="editReportData.name"
                  placeholder="Victim Full Name"
                  class="pt-1 text-des"
                ></v-text-field>
              </v-flex>

              <v-flex xs12 sm6 md6 lg6 pl-sm-2 class="pb-3">
                <span class="tablehead" style="color:black">Contact Phone</span>
                <v-text-field
                  outlined
                  dense
                  hide-details
                  type="number"
                  v-model="editReportData.phone"
                  placeholder="10-digit Phone Number"
                  class="pt-1 text-des"
                ></v-text-field>
              </v-flex>

              <v-flex xs12 sm6 md6 lg6 pr-sm-2 class="pb-3">
                <span class="tablehead" style="color:black">Gender / Sex</span>
                <v-select
                  outlined
                  dense
                  hide-details
                  :items="['Male', 'Female', 'Other']"
                  v-model="editReportData.sex"
                  class="pt-1 text-des"
                ></v-select>
              </v-flex>

              <v-flex xs12 sm6 md6 lg6 pl-sm-2 class="pb-3">
                <span class="tablehead" style="color:black">Poison Type</span>
                <v-text-field
                  outlined
                  dense
                  hide-details
                  v-model="editReportData.poisontype"
                  placeholder="e.g. Spectacled Cobra, Common Krait, Non-Venomous"
                  class="pt-1 text-des"
                ></v-text-field>
              </v-flex>

              <v-flex xs12 sm6 md6 lg6 pr-sm-2 class="pb-3">
                <span class="tablehead" style="color:black">Incident Location / Where</span>
                <v-text-field
                  outlined
                  dense
                  hide-details
                  v-model="editReportData.where"
                  placeholder="e.g. In paddy field, near home, forest"
                  class="pt-1 text-des"
                ></v-text-field>
              </v-flex>

              <v-flex xs12 sm6 md6 lg6 pl-sm-2 class="pb-3">
                <span class="tablehead" style="color:black">Bite Site / Body Part</span>
                <v-text-field
                  outlined
                  dense
                  hide-details
                  v-model="editReportData.sankebitesite"
                  placeholder="e.g. Left foot, Right hand"
                  class="pt-1 text-des"
                ></v-text-field>
              </v-flex>

              <v-flex xs12 sm6 md6 lg6 pr-sm-2 class="pb-3">
                <span class="tablehead" style="color:black">Victim Status / Condition</span>
                <v-text-field
                  outlined
                  dense
                  hide-details
                  v-model="editReportData.status"
                  placeholder="e.g. Stable, Critical, Recovering, Deceased"
                  class="pt-1 text-des"
                ></v-text-field>
              </v-flex>

              <v-flex xs12 sm6 md6 lg6 pl-sm-2 class="pb-3">
                <span class="tablehead" style="color:black">Hospital Status</span>
                <v-text-field
                  outlined
                  dense
                  hide-details
                  v-model="editReportData.status_hospital"
                  placeholder="e.g. Admitted, ICU, Discharged, Referred"
                  class="pt-1 text-des"
                ></v-text-field>
              </v-flex>

              <v-flex xs12 sm6 md6 lg6 pr-sm-2 class="pb-3">
                <span class="tablehead" style="color:black">ASV Vials Administered</span>
                <v-text-field
                  outlined
                  dense
                  hide-details
                  type="number"
                  v-model="editReportData.no_asv"
                  placeholder="0"
                  class="pt-1 text-des"
                ></v-text-field>
              </v-flex>

              <v-flex xs12 sm6 md6 lg6 pl-sm-2 class="pb-3">
                <span class="tablehead" style="color:black">Time of Incident</span>
                <v-text-field
                  outlined
                  dense
                  hide-details
                  type="datetime-local"
                  v-model="editReportData.timeofincident"
                  class="pt-1 text-des"
                ></v-text-field>
              </v-flex>

              <v-flex xs12 class="pb-3">
                <span class="tablehead" style="color:black">Notes</span>
                <v-textarea
                  outlined
                  dense
                  hide-details
                  rows="2"
                  v-model="editReportData.notes"
                  placeholder="Incident notes or summary"
                  class="pt-1 text-des"
                ></v-textarea>
              </v-flex>

              <v-flex xs12 class="pb-3">
                <span class="tablehead" style="color:black">Additional Info</span>
                <v-textarea
                  outlined
                  dense
                  hide-details
                  rows="2"
                  v-model="editReportData.info"
                  placeholder="Any additional information"
                  class="pt-1 text-des"
                ></v-textarea>
              </v-flex>
            </v-layout>
          </v-form>
        </v-card-text>
        <v-card-actions class="pa-4 justify-end">
          <v-btn tile outlined color="#FF1313" @click="editReportDialog = false">
            Cancel
          </v-btn>
          <v-btn tile color="#427D2D" depressed @click="submitEditReport()">
            <span style="color:white;">Save & Log Edit</span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
export default {
  data() {
    return {
      doctorsModal: false,
      selectedHospitalName: "",
      selectedHospitalDoctors: [],
      appLoading: false,
      ServerError: false,
      showSnackBar: false,
      timeout: 5000,
      msg: null,
      editReportDialog: false,
      editReportData: {
        id: "",
        name: "",
        phone: "",
        sex: "Male",
        poisontype: "Unknown / Unspecified",
        where: "",
        status: "",
        status_hospital: "",
        sankebitesite: "",
        no_asv: 0,
        timeofincident: "",
        notes: "",
        info: ""
      },

      data: {},
      alert: null,
      actions: [],
      recipients: [],
      currentUser: null,
      hospitals: [],
      hospitalSearchMode: "district",
      actionTypes: [
        "Contacted Hospital",
        "Dispatched Ambulance",
        "Contacted Victim",
        "Contacted Rescuer",
        "Anti-venom Arranged",
        "Victim Admitted",
        "Referred to Higher Centre",
        "Field Visit",
        "Other",
      ],

      radius: 50000,
      radiusOptions: [
        { text: "Within 10 km", value: 10000 },
        { text: "Within 25 km", value: 25000 },
        { text: "Within 50 km", value: 50000 },
        { text: "Within 100 km", value: 100000 },
      ],

      actionDialog: false,
      deleteDialog: false,
      closeDialog: false,
      closeRemarks: "",
      editId: null,
      deleteId: null,
      menuActionDate: false,
      menuActionTime: false,
      form: {
        id: null,
        actionType: "Contacted Hospital",
        actionDate: null,
        actionTime: null,
        description: "",
        hospital: null,
        hospitalName: null,
        contactedNumber: null,
        details: [],
      },

      map: null,
      markers: [],
    };
  },
  computed: {
    canEditReport() {
      const r1 = (this.userRole || "").toLowerCase().replace(/[^a-z0-9]/g, "");
      const r2 = (this.mainRole || "").toLowerCase().replace(/[^a-z0-9]/g, "");
      const localRole = (localStorage.getItem("userRole") || "").toLowerCase().replace(/[^a-z0-9]/g, "");
      const valid = [
        "admin",
        "medicalofficer",
        "districtmedicalofficer",
        "dmo",
        "statemedicalofficer",
        "statedisasterofficer",
        "statedisastermanagementofficer",
        "disastermanagementofficer",
        "districtemergencyofficer",
        "disasterofficer"
      ];
      return valid.indexOf(r1) !== -1 || valid.indexOf(r2) !== -1 || valid.indexOf(localRole) !== -1;
    },
    minActionDate() {
      if (this.data && (this.data.reporteddate || this.data.timeofincident)) {
        return this.toDateInput(this.data.reporteddate || this.data.timeofincident);
      }
      return null;
    },
    maxActionDate() {
      return this.toDateInput(new Date());
    },
    actionDescriptionLabel() {
      var type = this.form ? this.form.actionType : "";
      if (type === "Other") return "Other Response / Action Details";
      if (type === "Contacted Hospital") return "Hospital Contact Details";
      if (type === "Dispatched Ambulance") return "Ambulance Dispatch Details";
      if (type === "Contacted Victim") return "Victim Contact Notes";
      if (type === "Contacted Rescuer") return "Rescuer Contact Details";
      if (type === "Anti-venom Arranged") return "Anti-Venom Supply Details";
      if (type === "Victim Admitted") return "Admission Details";
      if (type === "Referred to Higher Centre") return "Referral Details";
      if (type === "Field Visit") return "Field Visit Report";
      return "Description";
    },
    actionDescriptionPlaceholder() {
      var type = this.form ? this.form.actionType : "";
      if (type === "Other") return "Specify the custom response or action taken...";
      if (type === "Contacted Hospital") return "e.g. Spoke with Dr. Sharma at DHH, confirmed ASV stock availability";
      if (type === "Dispatched Ambulance") return "e.g. 108 Ambulance (OD-02-X-1234) dispatched, ETA 15 mins";
      if (type === "Contacted Victim") return "e.g. Called victim's family, advised first-aid & non-panic protocol";
      if (type === "Contacted Rescuer") return "e.g. Rescuer Ramesh contacted, heading to site for snake capture";
      if (type === "Anti-venom Arranged") return "e.g. 10 ASV vials reserved at CHC pharmacy";
      if (type === "Victim Admitted") return "e.g. Victim admitted to ICU Ward 3, treatment initiated";
      if (type === "Referred to Higher Centre") return "e.g. Referred to SCB Medical College Cuttack for advanced care";
      if (type === "Field Visit") return "e.g. Medical team visited incident site in village";
      return "e.g. Contacted District Headquarters Hospital, ambulance dispatched";
    },
    userRole() {
      return this.$store.state.userRole;
    },
    mainRole() {
      return this.$store.state.mainRole;
    },
    //The District Medical Officer is the first responder on a snakebite, so they
    //are the only one who records actions against it. Login stores type in
    //userRole and role in mainRole; for officer accounts the backend sets both to
    //the same string (platformRouter.js), but check each so a mismatch can't lock
    //a genuine DMO out.
    isDmo() {
      const r1 = (this.userRole || "").toLowerCase().replace(/[^a-z0-9]/g, "");
      const r2 = (this.mainRole || "").toLowerCase().replace(/[^a-z0-9]/g, "");
      const valid = ["medicalofficer", "districtmedicalofficer", "dmo"];
      return valid.indexOf(r1) !== -1 || valid.indexOf(r2) !== -1;
    },
    //Gates every write control on this page: Record Action, Close Incident,
    //Log Contact on a hospital, and Add/Edit in the action log. Only District
    //Medical Officer / Medical Officer accounts are first responders with action privileges.
    canAct() {
      return this.canEditReport;
    },
    filteredActions() {
      if (!this.actions) return [];
      return this.actions.filter((a) => a.actionType !== "Report Edited");
    },
    editLogs() {
      if (!this.actions) return [];
      return this.actions.filter((a) => a.actionType === "Report Edited");
    },
    hasLocation() {
      return (
        this.data && this.data.location && this.data.location.length === 2
      );
    },
    /*
      The four timings are stages of one journey, not four unrelated numbers, so
      they render as a connected trail. A stage is reached the moment it has a
      timestamp; the first unreached one is called out as what the incident is
      waiting on, and everything past that stays quiet.
    */
    responseFlow() {
      var d = this.data || {};
      var reported = d.reporteddate || d.timeofincident;

      var stages = [
        {
          label: "Reported",
          icon: "mdi-bell-alert-outline",
          at: reported,
          //nothing precedes the report, so there is no interval to measure
          span: "Alert raised",
        },
        {
          label: "Acknowledged",
          icon: "mdi-eye-check-outline",
          at: d.acknowledgedDate,
          span:
            d.acknowledgedDate && reported
              ? this.calculateClosureDuration(reported, d.acknowledgedDate)
              : null,
        },
        {
          label: "First Response",
          icon: "mdi-run-fast",
          at: d.firstResponseDate,
          //responseTime is measured from when the alert went out rather than
          //from the report, so prefer it when the backend has computed it
          span:
            d.responseTime !== null && d.responseTime !== undefined
              ? this.humanize(d.responseTime)
              : d.firstResponseDate && reported
              ? this.calculateClosureDuration(reported, d.firstResponseDate)
              : null,
        },
        {
          label: "Closed",
          icon: "mdi-check-decagram-outline",
          at: d.closedDate,
          span:
            d.closedDate && reported
              ? this.calculateClosureDuration(reported, d.closedDate)
              : null,
        },
      ];

      var reached = -1;
      for (var i = 0; i < stages.length; i++) {
        if (stages[i].at) reached = i;
      }

      var self = this;
      return stages.map(function (s, index) {
        var done = !!s.at;
        return {
          label: s.label,
          icon: s.icon,
          state: done
            ? "is-done"
            : index === reached + 1
            ? "is-next"
            : "is-pending",
          value: done
            ? s.span || "Recorded"
            : index === reached + 1
            ? "Awaiting"
            : "—",
          stamp: done ? self.formatDateTime(s.at) : null,
        };
      });
    },
    //supporting facts: real, but not stages of the journey
    flowFacts() {
      var d = this.data || {};
      var nearest =
        this.hospitals && this.hospitals.length > 0 ? this.hospitals[0] : null;
      var count = d.actionCount || 0;

      var facts = [
        {
          icon: "mdi-clipboard-text-clock-outline",
          text: count + (count === 1 ? " action logged" : " actions logged"),
        },
      ];

      if (nearest) {
        facts.push({
          icon: "mdi-hospital-marker",
          text:
            nearest.distanceInKm !== null && nearest.distanceInKm !== undefined
              ? nearest.name + " · " + nearest.distanceInKm + " km"
              : nearest.name,
        });
      } else {
        facts.push({
          icon: "mdi-hospital-marker",
          text: "No hospitals found",
        });
      }

      return facts;
    },
    closureRemarks() {
      var d = this.data || {};
      return d.closeRemarks || d.closedRemarks || null;
    },
    detailFields() {
      var d = this.data || {};
      var fields = [
        { label: "Victim Name", value: d.name, icon: "mdi-account-outline" },
        { label: "Gender", value: d.sex, icon: "mdi-human-male-female" },
        { label: "Phone", value: d.phone || d.mobileno, icon: "mdi-phone-outline" },
        { label: "Poison Type", value: d.poisontype, icon: "mdi-flask-outline" },
        { label: "Incident Location", value: d.where, icon: "mdi-crosshairs-gps" },
        { label: "Victim Status", value: d.status, icon: "mdi-heart-pulse" },
        { label: "Hospital Status", value: d.status_hospital, icon: "mdi-hospital-building" },
        { label: "Bite Site", value: d.sankebitesite, icon: "mdi-bandage" },
        {
          label: "ASV Vials",
          value: d.no_asv !== null && d.no_asv !== undefined ? d.no_asv : null,
          icon: "mdi-needle",
        },
        {
          label: "Time of Incident",
          value: this.formatDateTime(d.timeofincident || d.reporteddate || d.created_at || d.createdAt),
          icon: "mdi-clock-outline",
        },
        { label: "District", value: d.Districtname, icon: "mdi-map-marker-outline" },
        {
          label: "Acknowledged",
          value: this.formatDateTime(d.acknowledgedDate),
          icon: "mdi-eye-check-outline",
        },
        {
          label: "First Response",
          value: this.formatDateTime(d.firstResponseDate),
          icon: "mdi-run-fast",
        },
      ];

      if (d.closedDate) {
        fields.push({
          label: "Closed At",
          value: this.formatDateTime(d.closedDate),
          icon: "mdi-check-decagram-outline",
        });
      }

      return fields;
    },
  },
  watch: {
    radius() {
      this.getHospitals();
    },
  },
  beforeMount() {
    this.getData();
  },
  methods: {
    openEditReportDialog() {
      if (!this.data) return;
      var d = this.data;
      this.editReportData = {
        id: d._id,
        name: d.name || "",
        phone: d.phone || d.mobileno || "",
        sex: d.sex || "",
        poisontype: d.poisontype || "",
        where: d.where || "",
        status: d.status || "",
        status_hospital: d.status_hospital || "",
        sankebitesite: d.sankebitesite || "",
        no_asv: d.no_asv !== undefined && d.no_asv !== null ? d.no_asv : 0,
        timeofincident: this.toDatetimeLocalInput(d.timeofincident || d.reporteddate || d.created_at || d.createdAt),
        notes: d.notes || d.note || "",
        info: d.info || ""
      };
      this.editReportDialog = true;
    },

    submitEditReport() {
      if (!this.editReportData.id) return;
      this.appLoading = true;
      axios({
        method: "POST",
        url: "/snakebite/alert/edit",
        headers: { "x-auth-token": localStorage.getItem("token") },
        data: this.editReportData
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status) {
            this.msg = response.data.msg || "Report updated successfully";
            this.showSnackBar = true;
            this.editReportDialog = false;
            this.getData();
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
    openDoctorsModal(hospital) {
      if (!hospital) return;
      this.selectedHospitalName = hospital.name || "Hospital";
      if (hospital.doctors && hospital.doctors.length > 0) {
        this.selectedHospitalDoctors = hospital.doctors;
        this.doctorsModal = true;
      } else {
        this.selectedHospitalDoctors = [];
        this.doctorsModal = true;
        axios({
          method: "GET",
          url: "/hospital/doctor/by-hospital/" + hospital._id,
          headers: { "x-auth-token": localStorage.getItem("token") }
        })
          .then((res) => {
            if (res.data && res.data.status) {
              this.selectedHospitalDoctors = res.data.data || [];
            }
          })
          .catch((e) => {
            console.log(e);
          });
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return "-";
      try {
        const d = new Date(dateStr);
        return d.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric"
        });
      } catch (e) {
        return dateStr;
      }
    },
    getData() {
      this.appLoading = true;
      axios({
        method: "GET",
        url: "/snakebite/alert/view",
        headers: { "x-auth-token": localStorage.getItem("token") },
        params: {
          id: this.$route.query.id,
          snakebiteId: this.$route.query.snakebiteId,
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status) {
            this.data = response.data.data || {};
            this.alert = response.data.alert;
            this.actions = response.data.actions || [];
            this.recipients = response.data.recipients || [];
            this.currentUser = response.data.currentUser || null;
            if (response.data.actionTypes && response.data.actionTypes.length > 0) {
              this.actionTypes = response.data.actionTypes;
            }
            this.getHospitals();
            this.$nextTick(this.initMap);
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },

    getHospitals() {
      if (!this.data || !this.data._id) return;
      axios({
        method: "GET",
        url: "/snakebite/nearest/hospitals",
        headers: { "x-auth-token": localStorage.getItem("token") },
        params: {
          snakebiteId: this.data._id,
          distance: this.radius,
          limit: 12,
        },
      })
        .then((response) => {
          if (response.data.status) {
            this.hospitals = response.data.data || [];
            this.hospitalSearchMode = response.data.searchMode;
            this.$nextTick(this.initMap);
          } else {
            this.hospitals = [];
          }
        })
        .catch((err) => {
          console.log(err);
        });
    },

    //the Maps script is loaded globally from public/index.html
    initMap() {
      if (!this.hasLocation) return;
      var maps = window.google && window.google.maps ? window.google.maps : null;
      if (!maps) return;
      var canvas = document.getElementById("alertmap");
      if (!canvas) return;

      var lon = this.data.location[0];
      var lat = this.data.location[1];

      this.map = new maps.Map(canvas, {
        zoom: 11,
        center: new maps.LatLng(lat, lon),
        mapTypeId: maps.MapTypeId.ROADMAP,
      });

      this.markers.forEach(function (m) {
        m.setMap(null);
      });
      this.markers = [];

      this.markers.push(
        new maps.Marker({
          map: this.map,
          position: new maps.LatLng(lat, lon),
          title: "Snakebite incident",
        })
      );

      var vm = this;
      this.hospitals.forEach(function (hospital) {
        if (!hospital.location || hospital.location.length !== 2) return;
        var marker = new maps.Marker({
          map: vm.map,
          position: new maps.LatLng(hospital.location[1], hospital.location[0]),
          title: hospital.name,
          icon: "https://maps.google.com/mapfiles/ms/icons/blue-dot.png",
        });
        var info = new maps.InfoWindow({
          content:
            '<div style="padding:6px"><b>' +
            hospital.name +
            "</b><br/>" +
            (hospital.mobileno || "") +
            "</div>",
        });
        marker.addListener("click", function () {
          info.open(vm.map, marker);
        });
        vm.markers.push(marker);
      });
    },

    openActionDialog() {
      this.editId = null;
      var now = new Date();
      this.form = {
        actionType: "Contacted Hospital",
        actionDate: this.toDateInput(now),
        actionTime: this.toTimeInput(now),
        description: "",
        hospital: null,
        hospitalName: null,
        contactedNumber: null,
        details: [],
      };
      this.actionDialog = true;
    },

    //"Call the hospital, then log it" in one step
    logHospitalContact(hospital) {
      var now = new Date();
      this.editId = null;
      this.form = {
        actionType: "Contacted Hospital",
        actionDate: this.toDateInput(now),
        actionTime: this.toTimeInput(now),
        description: "Contacted " + hospital.name,
        hospital: hospital._id,
        hospitalName: hospital.name,
        contactedNumber: hospital.mobileno,
        details: [],
      };
      this.actionDialog = true;
    },

    clearHospital() {
      this.form.hospital = null;
      this.form.hospitalName = null;
      this.form.contactedNumber = null;
    },

    editAction(action) {
      var stamp = action.actionTime ? new Date(action.actionTime) : new Date();
      this.editId = action._id;
      this.form = {
        actionType: action.actionType,
        actionDate: this.toDateInput(stamp),
        actionTime: this.toTimeInput(stamp),
        description: action.description,
        hospital: action.hospital && action.hospital._id ? action.hospital._id : action.hospital,
        hospitalName: action.hospitalName,
        contactedNumber: action.contactedNumber,
        details: (action.details || []).map(function (d) {
          return { key: d.key, value: d.value };
        }),
      };
      this.actionDialog = true;
    },

    addDetailRow() {
      this.form.details.push({ key: "", value: "" });
    },
    removeDetailRow(index) {
      this.form.details.splice(index, 1);
    },

    submitAction() {
      if (!this.form.description) {
        this.msg = "Please describe the action taken";
        this.showSnackBar = true;
        return;
      }
      if (this.form.actionDate) {
        if (this.minActionDate && this.form.actionDate < this.minActionDate) {
          this.msg = "Action date cannot be before reported date (" + this.minActionDate + ")";
          this.showSnackBar = true;
          return;
        }
        if (this.maxActionDate && this.form.actionDate > this.maxActionDate) {
          this.msg = "Action date cannot be after current date (" + this.maxActionDate + ")";
          this.showSnackBar = true;
          return;
        }
      }

      var details = this.form.details.filter(function (d) {
        return d.key && d.value;
      });

      var payload = {
        actionType: this.form.actionType,
        description: this.form.description,
        actionDate: this.form.actionDate,
        actionTime: this.form.actionTime,
        /*
          The pickers give a date and "HH:mm" read off this officer's clock, and
          only this machine knows which zone that is. Resolve it here and send
          the instant; left to the server, the same reading is resolved against
          the server's zone and comes back hours out.
        */
        actionTimestamp: this.toTimestamp(
          this.form.actionDate,
          this.form.actionTime
        ),
        hospital: this.form.hospital,
        contactedNumber: this.form.contactedNumber,
        details: details,
      };

      var url = "/snakebite/action/add";
      if (this.editId) {
        url = "/snakebite/action/edit";
        payload.id = this.editId;
      } else {
        payload.snakebiteId = this.data._id;
      }

      this.appLoading = true;
      axios({
        method: "POST",
        url: url,
        headers: { "x-auth-token": localStorage.getItem("token") },
        data: payload,
      })
        .then((response) => {
          this.appLoading = false;
          this.msg = response.data.msg;
          this.showSnackBar = true;
          if (response.data.status) {
            this.actionDialog = false;
            this.getData();
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },

    //only the officer who logged an action (or admin) may change it.
    //admin's moderation override is checked before canAct: admin no longer records
    //actions, but keeps the ability to correct or remove one that was recorded.
    canEditAction(action) {
      if (!action) return false;
      if (this.canAct || this.canEditReport) return true;
      if (this.currentUser) {
        var mine = action.actionBy && action.actionBy._id ? action.actionBy._id : action.actionBy;
        if (mine && String(mine) === String(this.currentUser)) return true;
      }
      var storedId = localStorage.getItem("id") || localStorage.getItem("userId") || (this.$store.state && this.$store.state.userId);
      if (storedId) {
        var actionBy = action.actionBy && action.actionBy._id ? action.actionBy._id : action.actionBy;
        if (actionBy && String(actionBy) === String(storedId)) return true;
      }
      return false;
    },

    confirmDeleteAction(action) {
      this.deleteId = action._id;
      this.deleteDialog = true;
    },

    deleteAction() {
      this.appLoading = true;
      axios({
        method: "POST",
        url: "/snakebite/action/delete",
        headers: { "x-auth-token": localStorage.getItem("token") },
        data: { id: this.deleteId },
      })
        .then((response) => {
          this.appLoading = false;
          this.deleteDialog = false;
          this.msg = response.data.msg;
          this.showSnackBar = true;
          if (response.data.status) this.getData();
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },

    closeIncident() {
      this.appLoading = true;
      axios({
        method: "POST",
        url: "/snakebite/alert/close",
        headers: { "x-auth-token": localStorage.getItem("token") },
        data: { snakebiteId: this.data._id, remarks: this.closeRemarks },
      })
        .then((response) => {
          this.appLoading = false;
          this.closeDialog = false;
          this.msg = response.data.msg;
          this.showSnackBar = true;
          if (response.data.status) this.getData();
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },

    resendAlert() {
      this.appLoading = true;
      axios({
        method: "POST",
        url: "/snakebite/alert/resend",
        headers: { "x-auth-token": localStorage.getItem("token") },
        data: { snakebiteId: this.data._id },
      })
        .then((response) => {
          this.appLoading = false;
          this.msg = response.data.msg;
          this.showSnackBar = true;
          if (response.data.status) this.getData();
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },

    statusClass(status) {
      if (status === "Closed") return "status-closed";
      if (status === "In Progress") return "status-progress";
      if (status === "Acknowledged") return "status-ack";
      return "status-new";
    },

    //gives each timeline entry a glyph that matches what was actually done
    actionIcon(type) {
      var icons = {
        "Contacted Hospital": "mdi-hospital-box-outline",
        "Dispatched Ambulance": "mdi-ambulance",
        "Contacted Victim": "mdi-account-heart-outline",
        "Contacted Rescuer": "mdi-account-hard-hat",
        "Anti-venom Arranged": "mdi-needle",
        "Victim Admitted": "mdi-bed-outline",
        "Referred to Higher Centre": "mdi-share-outline",
        "Field Visit": "mdi-map-marker-check-outline",
        "Report Edited": "mdi-file-document-edit-outline",
        Other: "mdi-dots-horizontal",
      };
      return icons[type] || "mdi-clipboard-text-outline";
    },

    humanize(minutes) {
      if (minutes === null || minutes === undefined || isNaN(minutes)) return "-";
      var total = Math.round(minutes);
      if (total < 60) return total + "m";
      var hours = Math.floor(total / 60);
      var mins = total % 60;
      if (hours < 24) return hours + "h " + mins + "m";
      var days = Math.floor(hours / 24);
      return days + "d " + (hours % 24) + "h";
    },

    calculateClosureDuration(start, end) {
      if (!start || !end) return null;
      var startDate = new Date(start);
      var endDate = new Date(end);
      var diffMs = endDate - startDate;
      if (diffMs <= 0 || isNaN(diffMs)) return "0m";
      var totalMinutes = Math.round(diffMs / (1000 * 60));
      return this.humanize(totalMinutes);
    },

    pad(n) {
      return n < 10 ? "0" + n : "" + n;
    },

    toDateInput(date) {
      if (!date) return null;
      var d = date instanceof Date ? date : new Date(date);
      if (isNaN(d.getTime())) return null;
      return (
        d.getFullYear() +
        "-" +
        this.pad(d.getMonth() + 1) +
        "-" +
        this.pad(d.getDate())
      );
    },

    toDatetimeLocalInput(date) {
      if (!date) return "";
      var d = date instanceof Date ? date : new Date(date);
      if (isNaN(d.getTime())) return "";
      return (
        d.getFullYear() +
        "-" +
        this.pad(d.getMonth() + 1) +
        "-" +
        this.pad(d.getDate()) +
        "T" +
        this.pad(d.getHours()) +
        ":" +
        this.pad(d.getMinutes())
      );
    },

    toTimeInput(date) {
      return this.pad(date.getHours()) + ":" + this.pad(date.getMinutes());
    },

    toTimestamp(dateStr, timeStr) {
      if (!dateStr) return new Date().toISOString();
      var time = timeStr || "00:00";
      var parts = String(dateStr).split("T")[0].split("-");
      var timeParts = String(time).split(":");
      if (parts.length === 3 && timeParts.length >= 2) {
        var year = parseInt(parts[0], 10);
        var month = parseInt(parts[1], 10) - 1;
        var day = parseInt(parts[2], 10);
        var hours = parseInt(timeParts[0], 10) || 0;
        var minutes = parseInt(timeParts[1], 10) || 0;
        var d = new Date(year, month, day, hours, minutes);
        if (!isNaN(d.getTime())) return d.toISOString();
      }
      return new Date(dateStr + " " + time).toISOString();
    },

    formatDateTime(value) {
      if (!value) return null;
      var d = new Date(value);
      if (isNaN(d.getTime())) return null;
      var h = d.getHours();
      //midnight and noon both read as 12 on a 12-hour clock, not 0
      var hour12 = h % 12 === 0 ? 12 : h % 12;
      return (
        this.pad(d.getDate()) +
        "-" +
        this.pad(d.getMonth() + 1) +
        "-" +
        d.getFullYear() +
        " " +
        this.pad(hour12) +
        ":" +
        this.pad(d.getMinutes()) +
        " " +
        (h < 12 ? "AM" : "PM")
      );
    },
  },
};
</script>

<style scoped>
/* ================= design tokens =================
   Same emerald system as the dashboards' stat cards (components/Common/statCard.vue)
   and the sidebar's active nav item, so this page reads as part of the same app. */
.alert-shell {
  --accent: #10b981;
  --ink: #047857;
  --tint: #f0fdf9;
  --tint-line: #d2efe3;
  --badge: #d1fae5;
  --line: #e7eef3;
  --muted: #64748b;
  --shadow: 0 1px 2px rgba(15, 23, 42, 0.04), 0 8px 24px -12px rgba(15, 23, 42, 0.1);

  background: #f6f8fb;
  min-height: 100vh;
  padding-bottom: 2rem;
}

/* ================= typography ================= */
.cardtitle {
  font-family: poppinssemibold, poppinsregular, sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: #1a2e23;
  letter-spacing: -0.1px;
}
.subtext {
  font-family: poppinsregular, sans-serif;
  font-size: 12.5px;
  color: var(--muted);
  line-height: 1.45;
}
.itemKey {
  font-family: poppinsmedium, poppinsregular, sans-serif;
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: #8496a5;
}
.itemValue {
  font-family: poppinsregular, sans-serif;
  font-size: 13.5px;
  color: #1b2a17;
  line-height: 1.4;
  word-break: break-word;
}
.nodata {
  font-family: poppinsregular, sans-serif;
  font-size: 13px;
  color: #8a998a;
}
.btntext {
  font-family: poppinsregular, sans-serif;
  font-size: 11.5px;
}
.pending-text {
  color: #b26a00;
}
/* only the disabled Alert Recipients table uses these */
.tablehead {
  font-family: poppinsmedium, poppinsregular, sans-serif;
  font-size: 11px !important;
  letter-spacing: 0.5px;
  color: #8496a5 !important;
  text-transform: uppercase !important;
  white-space: nowrap;
}
.tableitems {
  font-family: poppinsregular, sans-serif;
  font-size: 12.5px;
  white-space: nowrap;
}

/* ================= buttons ================= */
.btnlabel {
  font-family: poppinsmedium, poppinsregular, sans-serif;
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: 0.2px;
}
.btn-solid.v-btn {
  border-radius: 20px !important;
  background-color: #047857 !important;
  background: linear-gradient(135deg, var(--accent, #10b981) 0%, var(--ink, #047857) 100%) !important;
  box-shadow: 0 4px 12px -4px rgba(5, 150, 105, 0.45) !important;
  text-transform: none !important;
  letter-spacing: normal !important;
  transition: filter 0.25s ease, box-shadow 0.25s ease, transform 0.25s ease;
}
/* the phone button renders as an <a>, and appStyle.css forces anchors to #000 */
.btn-solid.v-btn,
.btn-solid.v-btn .btnlabel,
.btn-solid.v-btn .v-icon {
  color: #ffffff !important;
}
.btn-solid.v-btn:hover {
  filter: brightness(1.08);
  box-shadow: 0 6px 16px -4px rgba(5, 150, 105, 0.5) !important;
  transform: translateY(-1px);
}
.btn-solid.v-btn.v-btn--disabled {
  background: #eef2f6 !important;
  box-shadow: none !important;
  transform: none;
}
.btn-solid.v-btn.v-btn--disabled,
.btn-solid.v-btn.v-btn--disabled .btnlabel,
.btn-solid.v-btn.v-btn--disabled .v-icon {
  color: #9aa8b5 !important;
}
.btn-ghost.v-btn {
  border-radius: 20px !important;
  background: #ffffff !important;
  border: 1px solid var(--tint-line, #d2efe3) !important;
  box-shadow: none !important;
  text-transform: none !important;
  letter-spacing: normal !important;
  transition: background 0.25s ease, border-color 0.25s ease;
}
.btn-ghost.v-btn,
.btn-ghost.v-btn .btnlabel,
.btn-ghost.v-btn .v-icon {
  color: var(--ink, #047857) !important;
}
.btn-ghost.v-btn:hover {
  background: var(--tint, #f0fdf9) !important;
  border-color: var(--accent, #10b981) !important;
}

/* ================= hero ================= */
.alert-hero {
  position: relative;
  overflow: hidden;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  padding: 22px 26px 22px 30px;
  border: 1px solid var(--tint-line);
  border-radius: 18px;
  background: linear-gradient(135deg, #ffffff 0%, var(--tint) 100%);
  box-shadow: var(--shadow);
}
.hero-bar {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 5px;
  background: linear-gradient(180deg, var(--accent) 0%, var(--ink) 100%);
}
.hero-left {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  min-width: 0;
  flex: 1 1 340px;
}
.hero-badge {
  flex: 0 0 auto;
  width: 52px;
  height: 52px;
  border-radius: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, var(--accent) 0%, var(--ink) 100%);
  box-shadow: 0 6px 18px -6px rgba(5, 150, 105, 0.55);
}
.hero-badge .v-icon {
  color: #ffffff !important;
}
.hero-copy {
  min-width: 0;
}
.hero-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-family: poppinsmedium, poppinsregular, sans-serif;
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: var(--ink);
}
.hero-eyebrow .v-icon {
  color: var(--accent) !important;
}
.hero-title {
  margin-top: 2px;
  font-family: poppinssemibold, poppinsregular, sans-serif;
  font-size: 25px;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: -0.6px;
  color: #14251c;
  word-break: break-word;
}
.hero-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}
.meta-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 11px;
  border: 1px solid var(--tint-line);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.75);
  font-family: poppinsregular, sans-serif;
  font-size: 11.5px;
  color: #45564d;
  white-space: nowrap;
}
.meta-chip .v-icon {
  color: var(--accent) !important;
}
.hero-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
  flex: 0 0 auto;
}
.hero-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
}

/* ================= status chip ================= */
.status-chip {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border-radius: 16px;
  padding: 5px 14px;
  font-family: poppinsmedium, poppinsregular, sans-serif;
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: 0.3px;
  white-space: nowrap;
}
.status-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: currentColor;
}
.status-new {
  background: #fdecea;
  color: #c62828;
  box-shadow: 0 0 0 3px rgba(198, 40, 40, 0.08);
}
.status-ack {
  background: #fff4e0;
  color: #b26a00;
  box-shadow: 0 0 0 3px rgba(178, 106, 0, 0.08);
}
.status-progress {
  background: #e8f1fd;
  color: #1565c0;
  box-shadow: 0 0 0 3px rgba(21, 101, 192, 0.08);
}
.status-closed {
  background: #e8f5e9;
  color: #2e7d32;
  box-shadow: 0 0 0 3px rgba(46, 125, 50, 0.08);
}

/* ================= panels ================= */
.panel {
  margin-top: 20px;
  padding: 22px 24px 24px 24px;
  border: 1px solid var(--line);
  border-radius: 18px;
  background: #ffffff;
  box-shadow: var(--shadow);
}
.panel-head {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding-bottom: 16px;
  margin-bottom: 6px;
  border-bottom: 1px solid var(--line);
}
.panel-icon {
  flex: 0 0 auto;
  width: 38px;
  height: 38px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--badge);
}
.panel-icon .v-icon {
  color: var(--ink) !important;
}
.panel-copy {
  flex: 1 1 auto;
  min-width: 0;
  padding-top: 2px;
}
.panel-actions {
  flex: 0 0 auto;
}
.radius-select {
  max-width: 190px;
  display: inline-block;
}

/* ================= response flow =================
   Four stages of one journey on a single trail. Each stage owns the connector
   leaving it and colours it from its own state, so the emerald runs exactly as
   far as the incident actually got and stops dead at the first stage that has
   no timestamp. */
.flow-facts {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}
.fact-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid var(--tint-line);
  border-radius: 999px;
  background: var(--tint);
  font-family: poppinsmedium, poppinsregular, sans-serif;
  font-size: 11.5px;
  color: var(--ink);
  white-space: nowrap;
}
.fact-chip .v-icon {
  color: var(--accent) !important;
}

.flow {
  list-style: none;
  margin: 0;
  padding: 20px 0 2px 0;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
}
.flow-step {
  min-width: 0;
  text-align: center;
}
.flow-rail {
  position: relative;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.flow-node {
  position: relative;
  z-index: 1;
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid var(--line);
  background: #ffffff;
  color: #b6c4cf;
  transition: transform 0.25s ease;
}
.flow-node .v-icon {
  color: inherit !important;
}
/*
 * Spans from just clear of this node to just short of the next one: the 50%
 * offsets resolve against the column, so -50% reaches the neighbouring centre
 * and the 22px backs off the node's 20px radius plus its ring.
 */
.flow-line {
  position: absolute;
  top: 50%;
  left: calc(50% + 22px);
  right: calc(-50% + 22px);
  height: 2px;
  border-radius: 2px;
  background: var(--line);
  transform: translateY(-1px);
}
.flow-step.is-done .flow-node {
  border-color: transparent;
  background: linear-gradient(135deg, var(--accent) 0%, var(--ink) 100%);
  color: #ffffff;
  box-shadow: 0 6px 16px -6px rgba(5, 150, 105, 0.5);
}
.flow-step.is-done .flow-line {
  background: linear-gradient(90deg, var(--ink) 0%, var(--accent) 100%);
}
/* the stage the incident is actually waiting on */
.flow-step.is-next .flow-node {
  border-color: var(--accent);
  background: var(--tint);
  color: var(--ink);
}
.flow-copy {
  padding: 12px 6px 0 6px;
}
.flow-label {
  font-family: poppinsmedium, poppinsregular, sans-serif;
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: #8496a5;
}
.flow-value {
  margin-top: 5px;
  font-family: poppinssemibold, poppinsregular, sans-serif;
  font-size: 17px;
  color: #1a2e23;
  letter-spacing: -0.3px;
  line-height: 1.25;
  word-break: break-word;
}
.flow-step.is-pending .flow-value {
  color: #a7b6c2;
}
.flow-step.is-next .flow-value {
  color: var(--ink);
}
.flow-stamp {
  margin-top: 4px;
  font-family: poppinsregular, sans-serif;
  font-size: 11.5px;
  color: var(--muted);
}

/* narrow: the trail turns and runs down the page instead of across it */
@media (max-width: 760px) {
  .flow {
    grid-template-columns: minmax(0, 1fr);
    row-gap: 18px;
    padding-top: 14px;
  }
  .flow-step {
    display: grid;
    grid-template-columns: 40px minmax(0, 1fr);
    column-gap: 14px;
    align-items: start;
    text-align: left;
  }
  /* stretching the rail lets the connector cover its own row plus the gap */
  .flow-rail {
    align-self: stretch;
    height: auto;
    min-height: 40px;
    align-items: flex-start;
  }
  .flow-line {
    top: 42px;
    bottom: -18px;
    left: 50%;
    right: auto;
    width: 2px;
    height: auto;
    transform: translateX(-1px);
  }
  .flow-step.is-done .flow-line {
    background: linear-gradient(180deg, var(--ink) 0%, var(--accent) 100%);
  }
  .flow-copy {
    padding: 0;
  }
  .flow-value {
    font-size: 16px;
  }
  .flow-facts {
    justify-content: flex-start;
  }
}

/* ================= detail grid ================= */
.detail-cell {
  display: flex;
  align-items: flex-start;
  gap: 11px;
  height: 100%;
  padding: 12px 14px;
  border: 1px solid #edf2f6;
  border-radius: 12px;
  background: #fbfdfe;
  transition: border-color 0.25s ease, background 0.25s ease;
}
.detail-cell:hover {
  border-color: var(--tint-line);
  background: var(--tint);
}
.detail-icon {
  flex: 0 0 auto;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--badge);
}
.detail-icon .v-icon {
  color: var(--ink) !important;
}
.detail-copy {
  min-width: 0;
}
.detail-copy .itemValue {
  margin-top: 3px;
}

/* ================= notes / photo ================= */
.note-block {
  margin-top: 14px;
  padding: 14px 16px;
  border: 1px solid #edf2f6;
  border-left: 3px solid var(--accent);
  border-radius: 12px;
  background: #fbfdfe;
}
.note-block--closed {
  border-left-color: #2e7d32;
  background: #f4fbf5;
}
.note-head {
  display: flex;
  align-items: center;
  gap: 7px;
}
.note-head .v-icon {
  color: var(--ink) !important;
}
.note-body {
  margin-top: 6px;
  font-family: poppinsregular, sans-serif;
  font-size: 13.5px;
  color: #1b2a17;
  line-height: 1.55;
  white-space: pre-line;
}
.alert-photo {
  height: 170px;
  max-width: 100%;
  border-radius: 12px;
  border: 1px solid var(--line);
  object-fit: cover;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}
.alert-photo:hover {
  transform: scale(1.02);
  box-shadow: 0 10px 26px -12px rgba(15, 23, 42, 0.35);
}
.clickable {
  cursor: pointer;
}

/* ================= hospitals ================= */
.hospital-card {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 15px 16px;
  border: 1px solid #e9f1ec;
  border-radius: 14px;
  background: linear-gradient(150deg, #ffffff 0%, #fbfffd 100%);
  transition: transform 0.28s cubic-bezier(0.16, 1, 0.3, 1),
    box-shadow 0.28s ease, border-color 0.28s ease;
}
.hospital-card:hover {
  transform: translateY(-3px);
  border-color: var(--tint-line);
  box-shadow: 0 14px 28px -16px rgba(5, 150, 105, 0.4);
}
.hosp-top {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}
.hosp-icon {
  flex: 0 0 auto;
  width: 32px;
  height: 32px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--badge);
}
.hosp-icon .v-icon {
  color: var(--ink) !important;
}
.hosp-copy {
  flex: 1 1 auto;
  min-width: 0;
}
.hospital-name {
  font-family: poppinsmedium, poppinsregular, sans-serif;
  font-size: 13.5px;
  font-weight: 600;
  color: #1b2a17;
  line-height: 1.3;
}
.hospital-meta {
  font-family: poppinsregular, sans-serif;
  font-size: 11.5px;
  color: var(--muted);
  margin-top: 2px;
}
.distance-pill {
  flex: 0 0 auto;
  background: var(--badge);
  color: var(--ink);
  border-radius: 12px;
  padding: 2px 9px;
  font-family: poppinsmedium, poppinsregular, sans-serif;
  font-size: 10.5px;
  font-weight: 600;
  white-space: nowrap;
}
.unverified-tag {
  background: #fff4e0;
  color: #b26a00;
  border-radius: 6px;
  padding: 0 6px;
  font-size: 10px;
}
.hosp-addr {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-top: 10px;
  font-family: poppinsregular, sans-serif;
  font-size: 11.5px;
  color: var(--muted);
  line-height: 1.45;
}
.hosp-addr .v-icon {
  color: #9fb3a6 !important;
  margin-top: 1px;
}
.hosp-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: auto;
  padding-top: 14px;
}

/* ================= map ================= */
#alertmap {
  height: 380px;
  width: 100%;
  margin-top: 16px;
  border: 1px solid var(--line);
  border-radius: 14px;
  overflow: hidden;
}

/* ================= action log timeline ================= */
.timeline {
  padding-top: 18px;
}
.timeline-item {
  position: relative;
  padding-left: 30px;
  padding-bottom: 18px;
  border-left: 2px solid #e8eef2;
  margin-left: 13px;
}
.timeline-item:last-child {
  border-left-color: transparent;
  padding-bottom: 0;
}
.timeline-dot {
  position: absolute;
  left: -15px;
  top: 0;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  border: 2px solid var(--accent);
  box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.08);
}
.timeline-dot .v-icon {
  color: var(--ink) !important;
}
.timeline-body {
  padding: 13px 16px;
  border: 1px solid #edf2f6;
  border-radius: 12px;
  background: #fbfdfe;
  transition: border-color 0.25s ease, box-shadow 0.25s ease;
}
.timeline-body:hover {
  border-color: var(--tint-line);
  box-shadow: 0 8px 20px -14px rgba(15, 23, 42, 0.35);
}
.timeline-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}
.doctors-badge {
  background: linear-gradient(135deg, #f0fdf4 0%, #e6f4ea 100%);
  border: 1px solid #c8e6c9;
  border-radius: 8px;
  padding: 6px 10px;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 1px 3px rgba(66, 125, 45, 0.08);
}
.doctors-badge:hover {
  background: linear-gradient(135deg, #e6f4ea 0%, #dcedc8 100%);
  border-color: #a5d6a7;
  transform: translateY(-1px);
  box-shadow: 0 3px 6px rgba(66, 125, 45, 0.15);
}
.doc-icon-circle {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 2px rgba(0,0,0,0.1);
}
.doc-badge-text {
  font-family: poppinsmedium, sans-serif;
  font-size: 12px;
  color: #1b4332;
}
.doc-count-num {
  font-size: 13px;
  font-weight: 700;
  color: #2d6a4f;
}
.doc-view-btn {
  font-family: poppinssemibold, sans-serif;
  font-size: 11px;
  color: #427D2D;
  display: flex;
  align-items: center;
}
.cursor-pointer {
  cursor: pointer;
}

/* DOCTOR MODAL DESIGN */
.doc-modal-header {
  background: linear-gradient(135deg, #1b4332 0%, #2d6a4f 100%);
  color: #ffffff;
}
.header-icon-box {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(4px);
}
.doc-modal-title {
  font-family: poppinssemibold, sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: #ffffff;
  line-height: 1.2;
}
.doc-modal-subtitle {
  font-family: poppinsregular, sans-serif;
  font-size: 12px;
  color: #d8f3dc;
  display: flex;
  align-items: center;
  margin-top: 2px;
}
.close-btn-round {
  background: rgba(255, 255, 255, 0.15) !important;
  transition: all 0.2s ease;
}
.close-btn-round:hover {
  background: rgba(255, 255, 255, 0.3) !important;
}

/* DOCTOR ITEM CARD */
.doc-card-item {
  background: #ffffff;
  border: 1px solid #e2ece9;
  border-radius: 12px;
  transition: all 0.25s ease;
  box-shadow: 0 2px 6px rgba(27, 67, 50, 0.04);
}
.doc-card-item:hover {
  border-color: #b7e4c7;
  box-shadow: 0 4px 12px rgba(27, 67, 50, 0.1);
}
.doc-avatar-box {
  width: 46px;
  height: 46px;
  border-radius: 50%;
  background: #e8f5e9;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #b7e4c7;
}
.doc-name-text {
  font-family: poppinssemibold, sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: #1b4332;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}
.doc-spec-tag {
  font-family: poppinsregular, sans-serif;
  font-size: 11px;
  background: #e8f5e9;
  color: #2d6a4f;
  padding: 2px 8px;
  border-radius: 12px;
  font-weight: 500;
}
.doc-meta-info {
  font-family: poppinsregular, sans-serif;
  font-size: 12px;
  color: #555555;
}
.meta-item {
  display: inline-flex;
  align-items: center;
}

/* STATUS PILLS */
.status-pill-available {
  background: linear-gradient(135deg, #2e7d32 0%, #388e3c 100%);
  color: #ffffff;
  font-family: poppinssemibold, sans-serif;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  box-shadow: 0 2px 4px rgba(46, 125, 50, 0.2);
}
.status-pill-unavailable {
  background: linear-gradient(135deg, #e65100 0%, #f57c00 100%);
  color: #ffffff;
  font-family: poppinssemibold, sans-serif;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  box-shadow: 0 2px 4px rgba(230, 81, 0, 0.2);
}

/* CALL BUTTON */
.call-doc-btn {
  background-color: #2d6a4f !important;
  border-radius: 20px !important;
  padding: 0 14px !important;
  text-transform: none !important;
  letter-spacing: 0.3px;
  box-shadow: 0 2px 6px rgba(45, 106, 79, 0.3) !important;
  transition: all 0.2s ease !important;
}
.call-doc-btn:hover {
  background-color: #1b4332 !important;
  box-shadow: 0 4px 10px rgba(27, 67, 50, 0.4) !important;
}

/* EMPTY STATE */
.empty-doc-box {
  background: #f4fbf7;
  border: 1px dashed #b7e4c7;
  border-radius: 16px;
  padding: 24px;
}
.timeline-title {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  min-width: 0;
}
.timeline-tools {
  flex: 0 0 auto;
  display: flex;
  gap: 10px;
}
.action-type {
  font-family: poppinsmedium, poppinsregular, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #14251c;
}
.action-time {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: poppinsregular, sans-serif;
  font-size: 11px;
  color: var(--muted);
}
.action-time .v-icon {
  color: #9fb3a6 !important;
}
.action-desc {
  margin-top: 6px;
  font-family: poppinsregular, sans-serif;
  font-size: 13px;
  color: #1b2a17;
  line-height: 1.5;
}
.action-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 3px;
  margin-top: 7px;
  font-family: poppinsregular, sans-serif;
  font-size: 11px;
  color: #8496a5;
}
.action-meta .v-icon {
  color: #9fb3a6 !important;
  margin-right: 2px;
}
.detail-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
}
.detail-pill {
  display: inline-block;
  background: var(--tint);
  border: 1px solid var(--tint-line);
  border-radius: 9px;
  padding: 3px 10px;
  font-family: poppinsregular, sans-serif;
  font-size: 11px;
  color: #2f5a44;
}
.detail-pill b {
  color: var(--ink);
  font-weight: 600;
}

/* ================= empty states ================= */
.empty-state {
  padding: 26px 0 18px 0;
  text-align: center;
}
.empty-state .v-icon {
  color: #c3d2dc !important;
}

/* ================= dialogs ================= */
.dialog-title {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 18px 22px 14px 22px;
  border-bottom: 1px solid var(--line);
}
.dialog-title .v-icon {
  color: var(--ink) !important;
}

/* ================= responsive ================= */
@media (max-width: 700px) {
  .alert-hero {
    padding: 18px 18px 18px 22px;
  }
  .hero-right {
    align-items: flex-start;
    width: 100%;
  }
  .hero-actions {
    justify-content: flex-start;
  }
  .hero-title {
    font-size: 21px;
  }
  .panel {
    padding: 18px 16px 20px 16px;
  }
  .panel-head {
    flex-wrap: wrap;
  }
  .radius-select {
    max-width: 100%;
  }
  #alertmap {
    height: 280px;
  }
}
</style>

<style>
.v-dialog,
.v-dialog__content {
  --accent: #10b981;
  --ink: #047857;
  --tint: #f0fdf9;
  --tint-line: #d2efe3;
  --badge: #d1fae5;
  --line: #e7eef3;
  --muted: #64748b;
  --shadow: 0 1px 2px rgba(15, 23, 42, 0.04), 0 8px 24px -12px rgba(15, 23, 42, 0.1);
}
</style>
